import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_threads-shop-281/artifacts/qnpo4cbr_IMG_3858.webp";

const lines = ["Lace,", "lineage", "and"];
const lines2 = ["warm", "earth", "tones."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.19, 1, 0.22, 1], delay: 0.5 + i * 0.12 },
  }),
};

const MaskedLine = ({ words, offset = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      custom={offset}
      variants={lineVariant}
      initial="hidden"
      animate="show"
    >
      {words}
    </motion.span>
  </span>
);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 160]);
  const textY = useTransform(scrollY, [0, 800], [0, -60]);

  return (
    <section id="hero" className="relative min-h-screen w-full overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        {/* Text side */}
        <motion.div
          style={{ y: textY }}
          className="lg:col-span-7 flex flex-col justify-end px-6 md:px-12 pb-16 pt-40 z-10"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="text-xs uppercase tracking-[0.3em] text-aak-primary mb-8"
          >
            House of AAK — Atelier of Wearable Clothing
          </motion.p>

          <h1 className="font-serif font-light text-aak-fg text-6xl md:text-8xl lg:text-[7.5vw] leading-[0.92] tracking-tighter">
            <MaskedLine words={lines.join(" ")} offset={0} />
            <span className="italic text-aak-primary">
              <MaskedLine words={lines2.join(" ")} offset={1} />
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-10 max-w-md"
          >
            <p className="text-aak-mutedfg text-base leading-relaxed mb-8">
              Chantilly-lace salwar suits, farshi silhouettes, co-ord sets and
              anarkali dresses — hand-finished in small runs and dyed in the
              colour of warm ground.
            </p>
            <button
              data-testid="hero-shop-btn"
              onClick={() => scrollTo("shop")}
              className="group inline-flex items-center gap-4 bg-aak-fg text-aak-bg px-8 py-4 hover:bg-aak-primary transition-colors duration-500"
            >
              <span className="text-xs uppercase tracking-[0.25em]">Explore Collection</span>
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" strokeWidth={1.5} />
            </button>
          </motion.div>
        </motion.div>

        {/* Image side */}
        <div className="lg:col-span-5 relative overflow-hidden min-h-[50vh] lg:min-h-screen">
          <motion.div style={{ y: imgY }} className="absolute inset-0 -top-20 -bottom-20">
            <motion.img
              initial={{ scale: 1.2, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
              src={HERO_IMG}
              alt="AAK signature co-ord set"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-aak-bg/40 via-transparent to-transparent" />
          </motion.div>
          <div className="absolute bottom-8 left-8 z-10">
            <p className="font-serif italic text-aak-bg text-xl drop-shadow">No. 01 — Lemon Lace Co-ord</p>
          </div>
        </div>
      </div>
    </section>
  );
};
